<template>
    <ShopwareSidebar :open="sidebarOpen" @close="invokeCartSidebar()" title="Winkelmand">
        <template #title>Winkelmand ({{ count }})</template>
        <template #content>
            <CustomScrollbar class="h-full p-5" ref="scrollbar">
                <Transition name="slide">
                    <div v-if="addedToCart && !hasError" class="slide-wrapper">
                        <div class="mb-6 flex bg-succes-400 px-6 py-[25px]">
                            <div>
                                <CheckCircleIcon class="mr-2.5 size-6 stroke-2 text-succes-500" aria-label="Succes" />
                            </div>
                            <p class="text-sm font-light leading-[18px] text-succes-500">
                                Item succesvol aan het winkelmandje toegevoegd
                            </p>
                        </div>
                    </div>
                </Transition>

                <div class="my-[12px] gap-4 bg-error-400 px-[19px] py-[9px] lg:mt-0" v-if="hasError">
                    <p
                        class="flex items-center gap-2 text-sm font-light leading-[18px] tracking-[0.7px] text-error-500">
                        <ExclamationCircleIcon class="mr-2.5 size-6 stroke-2" />
                        {{ snippets['headless.cart.productOutOfStockErrorMessage'] }}
                    </p>
                </div>

                <ShopwareSideCartItem
                    v-for="product in cartItems"
                    :product
                    :productResponse="productDataMap[product.referencedId]"
                    :hasError="isProductAvailable(product.referencedId)"
                    :key="product.id"
                    @removeFromCart="updateScroll()"
                    @productLoaded="updateScroll()" />
            </CustomScrollbar>
        </template>
        <template #footer>
            <ShopwareSideCartTotal :totalPrice="totalPrice" :shippingTotal="shippingTotal" :subtotal="subtotal" />
        </template>
    </ShopwareSidebar>
</template>

<script setup lang="ts">
    import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/vue/24/outline';

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;
    const cartSidebarStore = useCartSidebarStore();
    const { sidebarOpen } = storeToRefs(cartSidebarStore);
    const { invokeCartSidebar } = cartSidebarStore;
    const addedToCart = ref(false);
    const hasError = ref(false);
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const { count, refreshCart, cartItems, totalPrice, shippingTotal, subtotal } = useCart();
    const { getErrorsCodes } = useCartNotification();
    const errors = ref([]);

    const cartProductsStore = useCartProductsStore();
    const { search: batchSearch } = useBatchProductSearch();
    const productDataMap = computed(() => cartProductsStore.productMap);

    watch(
        cartItems,
        async newItems => {
            if (newItems && newItems.length > 0) {
                await cartProductsStore.fetchProductsWithSearchFn(newItems, batchSearch);
            }
        },
        { immediate: true, deep: true },
    );

    const scrollbar = useTemplateRef('scrollbar');

    function updateScroll() {
        scrollbar?.value?.refresh();
    }

    // Watch for cart changes to show success message
    watch(count, (newCount, oldCount) => {
        updateScroll();
        if (newCount > oldCount) {
            addedToCart.value = true;
            updateScroll();

            // Clear existing timeout if any
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            // Hide message after 3 seconds
            timeoutId = setTimeout(() => {
                addedToCart.value = false;
                updateScroll();
            }, 3000);
        }
    });

    onMounted(async () => {
        await refreshCart();
        await nextTick();
        updateScroll();
        errors.value = getErrorsCodes();
    });

    onUpdated(() => {
        updateScroll();
    });

    onUnmounted(() => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    });

    function isProductAvailable(id) {
        return errors.value.find(error => error.key === id && error.messageKey === 'ndois-line-item-blocked');
    }
</script>

<style scoped>
    .slide-wrapper {
        overflow: hidden;
    }

    .slide-enter-active {
        transition:
            opacity 0.3s ease,
            max-height 0.3s ease;
    }

    .slide-leave-active {
        transition:
            opacity 0.3s ease,
            max-height 0.3s ease;
    }

    .slide-enter-from {
        opacity: 0;
        max-height: 0;
    }

    .slide-enter-to {
        opacity: 1;
        max-height: 200px;
    }

    .slide-leave-from {
        opacity: 1;
        max-height: 200px;
    }

    .slide-leave-to {
        opacity: 0;
        max-height: 0;
    }
</style>
